import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Copy, Check, Send, RotateCcw, User, Building2, Briefcase, Save } from 'lucide-react';
import { aiService } from '../services/api';
import { useToast, ToastContainer } from '../componets/Toast';

const GenerateMessageModal = ({ finding, onClose, onComplete }) => {
  const [messageType, setMessageType] = useState('follow_up');
  const [medium, setMedium] = useState('email');
  const [additionalContext, setAdditionalContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refinementInput, setRefinementInput] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const { toasts, removeToast, showSuccess, showError } = useToast();


  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);


  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem',
    },
    modal: {
      background: '#ffffff',
      borderRadius: '20px',
      padding: '0',
      maxWidth: '900px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      padding: '2rem 2rem 1.5rem 2rem',
      borderBottom: '2px solid #e2e8f0',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'start',
      marginBottom: '1rem',
    },
    title: {
      fontSize: '1.75rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '0.95rem',
      opacity: 0.95,
    },
    closeButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '8px',
      transition: 'all 200ms',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    findingPreview: {
      background: 'rgba(255, 255, 255, 0.15)',
      borderRadius: '12px',
      padding: '1rem',
      backdropFilter: 'blur(10px)',
    },
    findingTitle: {
      fontWeight: 700,
      fontSize: '1rem',
      marginBottom: '0.5rem',
    },
    findingDescription: {
      fontSize: '0.9rem',
      opacity: 0.9,
      lineHeight: 1.5,
    },
    contactInfo: {
      background: 'rgba(255, 255, 255, 0.15)',
      borderRadius: '12px',
      padding: '1rem',
      marginTop: '1rem',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '1rem',
      backdropFilter: 'blur(10px)',
    },
    contactInfoItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    contactInfoLabel: {
      fontSize: '0.85rem',
      opacity: 0.9,
    },
    scrollContent: {
      padding: '2rem',
      overflowY: 'auto',
      flex: 1,
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#0f172a',
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    select: {
      width: '100%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      cursor: 'pointer',
    },
    textarea: {
      width: '96%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    hint: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginTop: '0.5rem',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
      width: '100%',
    },
    resultSection: {
      marginTop: '2rem',
      padding: '1.5rem',
      background: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '16px',
    },
    resultHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    resultTitle: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: '#0f172a',
    },
    messageText: {
      fontSize: '0.95rem',
      lineHeight: 1.7,
      color: '#0f172a',
      whiteSpace: 'pre-wrap',
      marginBottom: '1rem',
      padding: '1rem',
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.75rem',
    },
    copyButton: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#ffffff',
      color: '#475569',
    },
    copyButtonCopied: {
      background: '#10b981',
      borderColor: '#10b981',
      color: 'white',
    },
    resetButton: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#ffffff',
      color: '#475569',
    },
    conversationContainer: {
      marginTop: '1.5rem',
      maxHeight: '300px',
      overflowY: 'auto',
      padding: '0.5rem',
    },
    messageItem: {
      marginBottom: '1rem',
      padding: '1rem',
      borderRadius: '12px',
    },
    userMessage: {
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      marginLeft: '2rem',
    },
    aiMessage: {
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      marginRight: '2rem',
    },
    messageLabel: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#64748b',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    refinementSection: {
      marginTop: '1.5rem',
      padding: '1.5rem',
      background: '#ffffff',
      border: '2px solid #e2e8f0',
      borderRadius: '16px',
    },
    refinementLabel: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    refinementHint: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginBottom: '1rem',
    },
    refinementInput: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-end',
    },
    refinementTextarea: {
      flex: 1,
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      minHeight: '80px',
      resize: 'vertical',
    },
    sendButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.25rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
      whiteSpace: 'nowrap',
      height: 'fit-content',
    },
    loadingDots: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
    },
    loadingDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      background: '#667eea',
    },
  };


  const handleSaveMessage = async () => {
    setSaving(true);
    
    try {
        const response = await fetch('http://localhost:5000/api/save-message', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
            contact_id: finding.contact_id,
            message_content: generatedMessage,
            message_type: messageType,
            medium: medium,
            finding_id: finding.id
        })
        });

        const data = await response.json();
        
        if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        showSuccess('Message saved successfully! 🎉'); // ← Add this
        } else {
        showError(data.error || 'Failed to save message'); // ← Add this
        }
    } catch (error) {
        console.error('Failed to save message:', error);
        showError('Failed to save message. Please try again.'); // ← Add this
    } finally {
        setSaving(false);
    }
    };

  const handleGenerate = async () => {
    setGenerating(true);
    setGeneratedMessage(null);
    setConversationHistory([]);
    
    try {
      const result = await aiService.generateMessageFromFinding(
        finding.id,
        messageType,
        medium,
        additionalContext
      );
      setGeneratedMessage(result.message);
      setConversationHistory([
        { role: 'assistant', content: result.message }
      ]);
    } catch (error) {
      console.error('Failed to generate:', error);
      alert('Failed to generate message. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleRefine = async () => {
    if (!refinementInput.trim()) return;

    const userMessage = refinementInput.trim();
    setRefinementInput('');
    setRefining(true);

    const updatedHistory = [
      ...conversationHistory,
      { role: 'user', content: userMessage }
    ];
    setConversationHistory(updatedHistory);

    try {
      const result = await aiService.refineMessage(
        finding.contact_id,
        messageType,
        medium,
        `Finding: ${finding.title}\n${finding.description}\n\nAdditional Context: ${additionalContext}`,
        updatedHistory
      );
      
      const newHistory = [
        ...updatedHistory,
        { role: 'assistant', content: result.message }
      ];
      setConversationHistory(newHistory);
      setGeneratedMessage(result.message);
    } catch (error) {
      console.error('Failed to refine:', error);
      const errorHistory = [
        ...updatedHistory,
        { role: 'assistant', content: 'Sorry, I had trouble refining the message. Please try again.' }
      ];
      setConversationHistory(errorHistory);
    } finally {
      setRefining(false);
    }
  };

    const handleCopy = () => {
    navigator.clipboard.writeText(generatedMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showSuccess('Copied to clipboard!'); // ← Add this
    };

  const handleReset = () => {
    setGeneratedMessage(null);
    setConversationHistory([]);
    setRefinementInput('');
    setCopied(false);
  };

  const saveButton = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.25rem',
    border: 'none',
    borderRadius: '12px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 200ms',
    fontFamily: "'Syne', sans-serif",
    background: '#8b5cf6',
    color: 'white',
    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
    };

    const saveButtonSaved = {
    background: '#10b981',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
    };

  const LoadingDots = () => (
    <div style={styles.loadingDots}>
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
      />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={styles.overlay}
      onClick={onClose}
    >
        <ToastContainer toasts={toasts} removeToast={removeToast} />

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <div style={{ flex: 1 }}>
              <h2 style={styles.title}>Generate Message from Finding</h2>
              <p style={styles.subtitle}>
                Create a timely message based on this opportunity
              </p>
            </div>
            <button
              onClick={onClose}
              style={styles.closeButton}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              <X size={24} />
            </button>
          </div>

          {/* Finding Preview */}
          <div style={styles.findingPreview}>
            <div style={styles.findingTitle}>{finding.title}</div>
            <div style={styles.findingDescription}>{finding.description}</div>
          </div>

          {/* Contact Info */}
          <div style={styles.contactInfo}>
            <div style={styles.contactInfoItem}>
              <User size={16} />
              <span style={styles.contactInfoLabel}>{finding.contact_name}</span>
            </div>
            {finding.contact_company && (
              <div style={styles.contactInfoItem}>
                <Building2 size={16} />
                <span style={styles.contactInfoLabel}>{finding.contact_company}</span>
              </div>
            )}
            {finding.contact_role && (
              <div style={styles.contactInfoItem}>
                <Briefcase size={16} />
                <span style={styles.contactInfoLabel}>{finding.contact_role}</span>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <div style={styles.scrollContent}>
          {/* Configuration Form */}
          {!generatedMessage && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>Message Type</label>
                <select
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value)}
                  style={styles.select}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.background = '#ffffff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="follow_up">Follow-up</option>
                  <option value="introduction">Introduction</option>
                  <option value="response">Response</option>
                  <option value="check_in">Check-in</option>
                  <option value="congratulations">Congratulations</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Communication Medium</label>
                <select
                  value={medium}
                  onChange={(e) => setMedium(e.target.value)}
                  style={styles.select}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.background = '#ffffff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="email">Email</option>
                  <option value="text">Text Message</option>
                  <option value="linkedin_message">LinkedIn Message</option>
                </select>
                <p style={styles.hint}>
                  {medium === 'email' && 'Professional and structured with subject line'}
                  {medium === 'text' && 'Casual and brief, like texting a friend'}
                  {medium === 'linkedin_message' && 'Professional but conversational'}
                </p>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Additional Context (Optional)</label>
                <textarea
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Add any extra details you want to include in the message..."
                  style={styles.textarea}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                    e.target.style.background = '#ffffff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <p style={styles.hint}>
                  The finding details are already included. Add anything else relevant.
                </p>
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating}
                style={{
                  ...styles.button,
                  opacity: generating ? 0.7 : 1,
                  cursor: generating ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!generating) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!generating) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }
                }}
              >
                <Sparkles size={20} />
                {generating ? 'Generating...' : 'Generate Message'}
              </button>
            </>
          )}

          {/* Loading State */}
          {generating && <LoadingDots />}

          {/* Generated Message Result */}
          {generatedMessage && !generating && (
            <>
              <div style={styles.resultSection}>
                <div style={styles.resultHeader}>
                  <div style={styles.resultTitle}>Generated Message</div>
                </div>
                
                <div style={styles.messageText}>{generatedMessage}</div>

                <div style={styles.buttonGroup}>
                    <button
                        onClick={handleCopy}
                        style={{
                        ...styles.copyButton,
                        ...(copied ? styles.copyButtonCopied : {})
                        }}
                        onMouseEnter={(e) => {
                        if (!copied) {
                            e.target.style.borderColor = '#cbd5e1';
                            e.target.style.background = '#f8fafc';
                        }
                        }}
                        onMouseLeave={(e) => {
                        if (!copied) {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.background = '#ffffff';
                        }
                        }}
                    >
                        {copied ? <Check size={18} /> : <Copy size={18} />}
                        {copied ? 'Copied!' : 'Copy Message'}
                    </button>
                    
                    <button
                        onClick={handleSaveMessage}
                        disabled={saving}
                        style={{
                        ...saveButton,
                        ...(saved ? saveButtonSaved : {}),
                        opacity: saving ? 0.6 : 1,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={(e) => {
                        if (!saving && !saved) {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.5)';
                        }
                        }}
                        onMouseLeave={(e) => {
                        if (!saving && !saved) {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
                        }
                        }}
                    >
                        {saved ? <Check size={18} /> : <Save size={18} />}
                        {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Message'}
                    </button>
                    
                    <button
                        onClick={handleReset}
                        style={styles.resetButton}
                        onMouseEnter={(e) => {
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.background = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#ffffff';
                        }}
                    >
                        <RotateCcw size={18} />
                        Start Over
                    </button>
                    </div>
              </div>

              {/* Refinement Section */}
              <div style={styles.refinementSection}>
                <div style={styles.refinementLabel}>Refine Message</div>
                <p style={styles.refinementHint}>
                  Ask for changes like "make it shorter", "add more enthusiasm", or "mention their company's recent achievement"
                </p>
                <div style={styles.refinementInput}>
                  <textarea
                    value={refinementInput}
                    onChange={(e) => setRefinementInput(e.target.value)}
                    placeholder="e.g., Make it more casual and mention coffee..."
                    style={styles.refinementTextarea}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.metaKey) {
                        handleRefine();
                      }
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      e.target.style.background = '#ffffff';
                      e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.background = '#f8fafc';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <button
                    onClick={handleRefine}
                    disabled={refining || !refinementInput.trim()}
                    style={{
                      ...styles.sendButton,
                      opacity: refining || !refinementInput.trim() ? 0.5 : 1,
                      cursor: refining || !refinementInput.trim() ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!refining && refinementInput.trim()) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!refining && refinementInput.trim()) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                      }
                    }}
                  >
                    <Send size={18} />
                    {refining ? 'Refining...' : 'Send'}
                  </button>
                </div>

                {/* Conversation History */}
                {conversationHistory.length > 1 && (
                  <div style={styles.conversationContainer}>
                    {conversationHistory.slice(1).map((msg, idx) => (
                      <div
                        key={idx}
                        style={{
                          ...styles.messageItem,
                          ...(msg.role === 'user' ? styles.userMessage : styles.aiMessage)
                        }}
                      >
                        <div style={styles.messageLabel}>
                          {msg.role === 'user' ? 'You' : 'AI'}
                        </div>
                        <div style={{ fontSize: '0.9rem', lineHeight: 1.6, color: '#0f172a' }}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GenerateMessageModal;